import React, { useState, useEffect } from "react";
import { Button, TextField } from "@mui/material";
import PropTypes from "prop-types";
import styles from "./RuleInput.module.css";
import RuleTypeDropdown from "./RuleTypeDropdown";

const RuleInput = ({
  className = "",
  ruleType,
  ruleDetails,
  onSave,
  onCancel,
  onDelete, // New prop for handling delete
  isNewRule = false, // New prop to indicate if the rule is new
}) => {
  const [type, setType] = useState(ruleType || "Keepers"); // Default to 'Keepers' if no ruleType is provided
  const [details, setDetails] = useState(ruleDetails || ""); // Initialize with props or empty string

  // Update state when props change (e.g., when editing a rule)
  useEffect(() => {
    setType(ruleType || "Keepers"); // Update type when ruleType prop changes
    setDetails(ruleDetails || ""); // Update details when ruleDetails prop changes
  }, [ruleType, ruleDetails]);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault(); // Prevent default form submission
    if (!type || !details) {
      console.error("Rule type and details are required");
      return; // Exit if validation fails
    }

    const ruleData = { type, details };
    onSave(ruleData); // Call the onSave function passed as a prop
  };

  // Handle delete action
  const handleDelete = () => {
    if (isNewRule) {
      console.error("Cannot delete a new rule that hasn't been saved.");
      return; // Exit gracefully if the rule is new
    }

    onDelete(); // Call the onDelete function passed as a prop
  };

  return (
    <form className={[styles.ruleinput, className].join(" ")} onSubmit={handleSubmit}>
      <div className={styles.ruleArea}>
        <div className={styles.ruleAreaChild} />
        <div className={styles.addRuleArea}>
          {/* Add Rule Header */}
        </div>
        <div className={styles.ruleDetailsArea}>
          {/* Dropdown for Rule Type */}
          <RuleTypeDropdown
            value={type}
            onChange={(e) => setType(e.target.value)} // Update type state
          />
          <div className={styles.ruleNameArea}>
            <div className={styles.ruleDetails}>Rule details</div>
          </div>
          {/* TextField for Rule Details */}
          <TextField
            className={styles.addDetailsArea}
            placeholder="Add details"
            variant="outlined"
            multiline
            rows={6}
            sx={{
              "& fieldset": { borderColor: "#d9d9d9" },
              "& .MuiInputBase-root": {
                backgroundColor: "#fff",
                borderRadius: "5px",
                fontSize: "14px",
              },
              "& .MuiInputBase-input": {
                color: "#000",
                paddingTop: "10px", // Align placeholder text at the top
              },
            }}
            value={details}
            onChange={(e) => setDetails(e.target.value)} // Update details state
          />
        </div>

        {/* Buttons for Save, Cancel, and Delete */}
        <div className={styles.specialPrimaryButtons}>
          <Button
            className={styles.buttonPrimary}
            disableElevation
            variant="contained"
            sx={{
              textTransform: "none",
              color: "#fff",
              fontSize: "14px",
              background: "#013369",
              borderRadius: "8px",
              "&:hover": { background: "#013369" },
              width: 65,
              height: 36,
            }}
            type="submit" // Ensure this is a submit button
          >
            Save
          </Button>
          <div className={styles.cancel} onClick={onCancel}>
            Cancel
          </div>
          {!isNewRule && ( // Hide the delete option if it's a new rule
          <div
            className={styles.deleteRule}
            onClick={handleDelete}
            disabled={isNewRule} // Disable the button if it's a new rule
          >
            Delete
          </div>
          )}
        </div>
      </div>
    </form>
  );
};

RuleInput.propTypes = {
  className: PropTypes.string,
  ruleType: PropTypes.string, // Add prop type for ruleType
  ruleDetails: PropTypes.string, // Add prop type for ruleDetails
  onSave: PropTypes.func.isRequired, // Ensure onSave is passed
  onCancel: PropTypes.func.isRequired, // Ensure onCancel is passed
  onDelete: PropTypes.func.isRequired, // Ensure onDelete is passed
  isNewRule: PropTypes.bool, // Indicate if the rule is new
};

export default RuleInput;
